import {
  useQueryClient,
  useInfiniteQuery,
  QueryClient,
  QueryFunction,
} from '@tanstack/react-query';
import { viewSearch } from '@simplywallst/services';
import { getQueryKey, normaliseResponse } from '@/hooks/useGridView';
import type { GridView } from '@/hooks/useGridView';
import { subDays } from 'date-fns/subDays';

type InvestingIdea = GridView & {
  isNew: boolean;
};

export type Response = {
  meta: {
    totalPages: number;
    totalRecords: number;
  };
  entities: Record<string, InvestingIdea>;
  keys: string[];
};

export const QUERY_KEY = ['INVESTING_IDEAS'] as const;

const PAGE_SIZE = 12;
const todayMinus90Days = subDays(new Date(), 90).getTime();

export const makeQueryFn =
  (
    queryClient: QueryClient
  ): QueryFunction<Response, typeof QUERY_KEY, number> =>
  async ({ pageParam = 1 }) => {
    const response = await viewSearch({
      offset: (pageParam - 1) * PAGE_SIZE,
      size: PAGE_SIZE,
    });

    const { data, meta } = response.data;
    const entities = data.reduce(
      (a, b) => {
        const safeId = b.id.toString();

        let isNew = false;
        if (typeof b.create_date === 'number') {
          isNew = b.create_date - todayMinus90Days >= 0;
        }

        const gridView = normaliseResponse(b);

        // Pre populate grid rules as it costs nothing
        if (b.type_slug !== 'narrative-collection') {
          const QUERY_KEY = getQueryKey(safeId);
          queryClient.setQueryData<GridView | undefined>(
            QUERY_KEY,
            () => gridView
          );
        }

        a[safeId] = {
          ...gridView,
          isNew,
        };
        return a;
      },
      {} as Response['entities']
    );

    const { total_records: totalRecords } = meta;

    return {
      meta: {
        totalPages: Math.ceil((totalRecords || 0) / PAGE_SIZE),
        totalRecords,
      },
      entities,
      keys: data.map((n) => n.id.toString()),
    };
  };

/**
 * ### Description
 *
 * This hook manages the Investing Ideas listing, an infinite list of grid views from `api/grid/view/search`
 *
 * - Retrieves the publicly listed grid views for Investing Ideas
 * - Pre-populates `GRID_VIEW_****` queries
 *
 */
export const useInvestingIdeas = () => {
  const queryClient = useQueryClient();
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetched,
    status,
  } = useInfiniteQuery({
    queryKey: QUERY_KEY,
    queryFn: makeQueryFn(queryClient),
    retry: false,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      allPages.length < lastPage.meta.totalPages ? allPages.length + 1 : null,
  });
  return {
    data,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetched,
    status,
  };
};
